import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Chip, Hairline, Icon, Swatch } from "@/components/primitives";
import { MobileFrame } from "@/components/MobileFrame";
import { FIG_ITEM } from "@/data/fig";
import { supabase } from "@/integrations/supabase/client";

interface FotwStampData {
  weekStart: string;
}

export default function Certificate() {
  const nav = useNavigate();
  const item = FIG_ITEM;
  const [searchParams] = useSearchParams();
  const scanId = searchParams.get("scanId");

  // FotW stamp data — only populated when the cert is opened for a real
  // scan that won a Find of the Week. The page otherwise renders the
  // static demo. Text-only stamp, no confidence score (spec).
  const [fotwStamp, setFotwStamp] = useState<FotwStampData | null>(null);
  useEffect(() => {
    if (!scanId) return;
    let cancelled = false;
    supabase
      .from("scans")
      .select("is_weekly_feature_winner, weekly_feature_week")
      .eq("id", scanId)
      .maybeSingle()
      .then(({ data }) => {
        if (cancelled) return;
        const row = data as
          | { is_weekly_feature_winner?: boolean; weekly_feature_week?: string | null }
          | null;
        if (row?.is_weekly_feature_winner && row.weekly_feature_week) {
          setFotwStamp({ weekStart: row.weekly_feature_week });
        }
      });
    return () => { cancelled = true; };
  }, [scanId]);

  const fotwLabel = fotwStamp
    ? new Date(fotwStamp.weekStart + "T00:00:00").toLocaleDateString("en-US", {
        month: "long", day: "numeric", year: "numeric",
      })
    : null;
  const custody = [
    { date: "Apr 18, 2026", actor: "@you", action: "Scanned & verified", loc: "Brooklyn, NY" },
    { date: "Apr 18, 2026", actor: "@you", action: "Listed on Depop", loc: "$98" },
  ];
  const archiveFacts = [
    { k: "First registered", v: "Apr 18, 2026" },
    { k: "Fig ID", v: "FIG-00241" },
    { k: "Archive matches", v: "12 similar" },
    { k: "Verified scans", v: "1 of 1" },
  ];
  // Stable QR-like grid
  const qrCells = Array.from({ length: 49 }, (_, i) => (i * 37 + 11) % 100 > 42);

  return (
    <MobileFrame>
      <div className="paper-bg" style={{ minHeight: "100vh", paddingBottom: 40 }}>
        <div style={{ padding: "12px 20px 0", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <button onClick={() => nav(-1)} style={{ background: "none", border: "none", padding: 6, cursor: "pointer", color: "hsl(var(--ink))" }}>{Icon.chev(18, "l")}</button>
          <span className="caps">fig.app/r/fig-00241</span>
          <div style={{ width: 30 }} />
        </div>

        <div style={{ padding: "14px 20px 0" }}>
          <div style={{ background: "hsl(var(--cream))", border: "2px double hsl(var(--ink))", padding: "18px 18px 20px", position: "relative" }}>
            <div style={{ position: "absolute", top: 10, right: 10 }}>
              <div style={{ width: 54, height: 54, background: "hsl(var(--ink))", padding: 4, display: "grid", gridTemplateColumns: "repeat(7,1fr)", gap: 1 }}>
                {qrCells.map((on, i) => (
                  <div key={i} style={{ background: on ? "hsl(var(--cream))" : "hsl(var(--ink))" }} />
                ))}
              </div>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 8, textAlign: "center", color: "hsl(var(--ink-2))", marginTop: 2, letterSpacing: "0.08em" }}>SCAN ME</div>
            </div>

            <div className="caps" style={{ color: "hsl(var(--rust))" }}>Fig Certificate</div>
            <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900, fontSize: 30, color: "hsl(var(--ink))", lineHeight: 0.95, letterSpacing: "-0.02em", marginTop: 4 }}>
              Authenticity<br/>Record
            </div>
            <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 13, color: "hsl(var(--ink-2))", marginTop: 6 }}>
              permanent · public · non-transferable
            </div>
            <Hairline style={{ margin: "14px 0 10px" }} />
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <Swatch kind="olive" style={{ width: 80, height: 80, border: "1px solid hsl(var(--ink))", flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 800, fontSize: 17, color: "hsl(var(--ink))", lineHeight: 1.1 }}>{item.title}</div>
                <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 12, color: "hsl(var(--ink-2))", marginTop: 2 }}>{item.brand} · {item.era}</div>
                <div style={{ marginTop: 6, display: "flex", gap: 4, flexWrap: "wrap" }}>
                  <Chip filled>VERIFIED</Chip>
                  <Chip>#{item.id}</Chip>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding: "18px 20px 0" }}>
          <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 6 }}>Archive facts</div>
          <div style={{ border: "1px solid hsl(var(--ink))", background: "hsl(var(--cream))" }}>
            {archiveFacts.map(({ k, v }, i, a) => (
              <div key={k} style={{ display: "flex", justifyContent: "space-between", padding: "10px 14px", borderBottom: i < a.length - 1 ? "1px dashed hsl(var(--sand-deep))" : "none" }}>
                <span className="caps">{k}</span>
                <span style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 14, color: "hsl(var(--ink))" }}>{v}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Find of the Week winner stamp — text only, no confidence score.
            Only renders when this cert is for a scan that actually won. */}
        {fotwStamp && fotwLabel && (
          <div style={{ padding: "18px 20px 0" }}>
            <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 6 }}>
              Find of the Week
            </div>
            <div style={{
              border: "2px solid hsl(var(--rust))",
              background: "hsl(var(--cream))",
              padding: "14px 16px",
              fontFamily: "'Fraunces', 'Playfair Display', serif",
              color: "hsl(var(--ink))",
            }}>
              <div style={{
                fontFamily: "'IBM Plex Mono', monospace",
                fontSize: 9, letterSpacing: "0.18em",
                color: "hsl(var(--rust))", textTransform: "uppercase",
              }}>
                FIG Community Vote · Winner
              </div>
              <div style={{
                fontWeight: 800, fontSize: 18, lineHeight: 1.2, marginTop: 6,
              }}>
                Find of the Week · Week of {fotwLabel}
              </div>
              <div style={{
                fontSize: 13, lineHeight: 1.45, color: "hsl(var(--ink-2))", marginTop: 4,
              }}>
                Selected by community vote among that week's verified posts.
              </div>
            </div>
          </div>
        )}

        <div style={{ padding: "18px 20px 0" }}>
          <div className="caps" style={{ color: "hsl(var(--rust))", marginBottom: 6 }}>Chain of custody</div>
          <div style={{ background: "hsl(var(--cream))", border: "1px solid hsl(var(--ink))", padding: "10px 14px" }}>
            {custody.map((c, i) => (
              <div key={i} style={{ display: "flex", gap: 10, padding: "8px 0", borderTop: i > 0 ? "1px dashed hsl(var(--sand-deep))" : "none" }}>
                <div style={{ width: 8, height: 8, borderRadius: "50%", background: "hsl(var(--rust))", marginTop: 6, flexShrink: 0 }} />
                <div style={{ flex: 1 }}>
                  <div style={{ display: "flex", justifyContent: "space-between" }}>
                    <span style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 14, color: "hsl(var(--ink))" }}>{c.action}</span>
                    <span className="caps">{c.date}</span>
                  </div>
                  <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 11, color: "hsl(var(--ink-2))", marginTop: 2 }}>
                    {c.actor} · {c.loc}
                  </div>
                </div>
              </div>
            ))}
            <div style={{ padding: "10px 0 4px", borderTop: "1px dashed hsl(var(--sand-deep))", fontFamily: "'Fraunces', 'Playfair Display', serif", fontSize: 12, color: "hsl(var(--ink-2))", textAlign: "center" }}>
              more entries appear as this garment changes hands
            </div>
          </div>
        </div>

        <div style={{ padding: "20px 20px 0" }}>
          <div style={{ background: "hsl(var(--ink))", color: "hsl(var(--ink-fg))", padding: "16px 18px", display: "flex", gap: 14, alignItems: "center" }}>
            <div style={{ width: 54, height: 72, background: "hsl(var(--paper))", color: "hsl(var(--ink))", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", flexShrink: 0, position: "relative" }}>
              <div style={{ position: "absolute", top: 4, left: "50%", transform: "translateX(-50%)", width: 6, height: 6, borderRadius: "50%", background: "hsl(var(--ink))" }} />
              <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 900, fontStyle: "italic", fontSize: 22, marginTop: 10, fontVariationSettings: '"opsz" 144, "SOFT" 100, "WONK" 1' }}>fig</div>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 7, letterSpacing: "0.1em", marginTop: 2 }}>NFC · 00241</div>
            </div>
            <div style={{ flex: 1 }}>
              <div className="caps" style={{ color: "hsl(var(--marigold))" }}>Fig Tag · NFC hangtag</div>
              <div style={{ fontFamily: "'Fraunces', 'Playfair Display', serif", fontWeight: 700, fontSize: 15, lineHeight: 1.2, marginTop: 2 }}>
                Seal this certificate to the garment, forever.
              </div>
              <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: "hsl(var(--sand))", marginTop: 4 }}>
                pack of 10 · $12 · ships free
              </div>
            </div>
          </div>
        </div>

        <div style={{ padding: "14px 20px 0", display: "flex", gap: 8 }}>
          <button className="btn btn-ghost" style={{ flex: 1 }}>Copy link</button>
          <button className="btn btn-primary" style={{ flex: 1 }}>Order Tags →</button>
        </div>

        <div style={{ padding: "18px 20px 0" }}>
          <Hairline dashed />
          <div style={{ fontFamily: "'IBM Plex Mono', monospace", fontSize: 10, color: "hsl(var(--ink-2))", marginTop: 8, lineHeight: 1.5, textAlign: "center" }}>
            Fig IDs are permanent. Verified listings attract 3.2× more views. Fig never re-issues an ID to a different garment.
          </div>
        </div>
      </div>
    </MobileFrame>
  );
}
